import { useState } from "react";
import ReactJson from "react-json-view";
import AceEditor from "react-ace";
import "ace-builds/src-noconflict/mode-json";
import "ace-builds/src-noconflict/theme-iplastic";
import "ace-builds/src-noconflict/ext-language_tools";
import "brace/ext/searchbox"; // для команд ctrl+f и ctrl+h
import { Button, FormControl, TextField } from "@mui/material";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { selectActiveRobotId } from "@/store/ses/sesRobotSlice";
import { clearTestingScript, selectTestingScript, testScript } from "@/store/ses/sesScriptSlice";
import { selectDebuggerSession } from "@/store/ses/sesSlice";
import { useTranslate } from "@/hooks";
import { colorPrimary } from "@/constants/colors";
import { RequestStatus, ResponseStatus } from "@/types/statusTypes";
import DraggablePopoverComponent from "@/components/MUI/DraggablePopoverComponent/DraggablePopoverComponent";
import ProgressCircle from "@/components/MUI/ProgressCircle/ProgressCircle";
import { IScriptTestProps } from "./ScriptTest.props";
import styles from "./ScriptTest.module.scss";
import { Hint } from "@/HOC";

const ScriptTest = ({ scriptOrDataElemId, changeFlg, useOfValue }: IScriptTestProps): JSX.Element => {
	const [anchorElTestResult, setAnchorElTestResult] = useState<HTMLElement | null>(null); // якорь для блока результата тестирования
	const [dataForTestingScript, setDataForTestingScript] = useState<string>(""); // данные для тестирования скрипта
	const [inputValue, setInputValue] = useState<string>(""); // значение

	const dispatch = useAppDispatch();
	const activeRobotId = useAppSelector(selectActiveRobotId); // store - id активного робота
	const testingScript = useAppSelector(selectTestingScript); // store - тест скрипт
	const debuggerSession = useAppSelector(selectDebuggerSession); // store - сессия робота

	const translate = useTranslate(); // hook для перевода текста

	// обработчик сбора данных текущей сессии
	const collectingCurrentSessionDataHandler = (): void => {
		debuggerSession && setDataForTestingScript(JSON.stringify(debuggerSession, null, "\t"));
	};

	// обработчик тестирования скрипта
	const testScriptHandler = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>): void => {
		setAnchorElTestResult(e.currentTarget);
		dispatch(clearTestingScript()); // очистка данных теста
		activeRobotId &&
			dataForTestingScript &&
			dispatch(
				testScript({
					data: dataForTestingScript,
					robotId: activeRobotId,
					scriptId: scriptOrDataElemId,
					value: useOfValue !== "notUsed" && inputValue ? inputValue : undefined,
				})
			); // тестирование
	};

	return (
		<div className={styles.scriptsTesting}>
			<AceEditor
				name="dataForTesting"
				placeholder={translate("title_dataForTestingTheScript")}
				mode="json"
				theme="iplastic"
				width="100%"
				fontSize={11}
				value={dataForTestingScript}
				onChange={(value) => setDataForTestingScript(value)}
				editorProps={{
					$blockScrolling: true,
				}}
				setOptions={{
					enableBasicAutocompletion: true,
					enableLiveAutocompletion: true,
					// enableSnippets: true,
					useWorker: false, // для json
					wrap: true,
				}}
				style={{ flexGrow: 1 }}
			/>

			{/* значение */}
			{useOfValue !== "notUsed" && (
				<FormControl fullWidth>
					<TextField
						required={useOfValue === "required"}
						label={translate("input_value")}
						variant="outlined"
						disabled={testingScript.status === RequestStatus.LOADING}
						value={inputValue}
						onChange={(e) => setInputValue(e.target.value)}
						error={useOfValue === "required" && !inputValue}
						InputProps={{
							style: {
								height: 33,
								fontSize: 13,
								color: colorPrimary,
							},
						}}
						InputLabelProps={{
							style: {
								fontSize: 13,
							},
						}}
						sx={{ '.MuiInputLabel-root[data-shrink="false"]': { top: -8 } }}
					/>
				</FormControl>
			)}

			<div className={styles.block}>
				{/* взять данные текущей сессии */}
				<FormControl sx={{ flexGrow: 1 }}>
					<Hint hint={translate("hint_sesGetCurrentSessionDataButton")}>
						<Button
							variant="outlined"
							disabled={debuggerSession === null}
							sx={{ fontSize: 11, overflow: "hidden", height: 33 }}
							onClick={collectingCurrentSessionDataHandler}
							fullWidth
						>
							{translate("button_getCurrentSessionData")}
						</Button>
					</Hint>
				</FormControl>
				{/* тест скрипта */}
				<FormControl sx={{ flexGrow: 1 }}>
					<Hint hint={translate("hint_sesScriptButtonTest")}>
						<Button
							variant="outlined"
							sx={{ fontSize: 11, overflow: "hidden", height: 33 }}
							disabled={
								!dataForTestingScript || testingScript.status === RequestStatus.LOADING || !scriptOrDataElemId || changeFlg || (useOfValue === "required" && !inputValue)
							}
							onClick={testScriptHandler}
							fullWidth
						>
							{translate("button_test")}
						</Button>
					</Hint>
				</FormControl>
			</div>

			<DraggablePopoverComponent
				anchorEl={anchorElTestResult}
				setAnchorEl={setAnchorElTestResult}
				header="title_result"
				anchorOrigin={{
					vertical: "top",
					horizontal: "right",
				}}
				transformOrigin={{
					vertical: "bottom",
					horizontal: "right",
				}}
				sx={{ width: "40vw" }}
			>
				{/* тестирование скрипта */}
				{testingScript.status === RequestStatus.LOADING && (
					<div>
						<ProgressCircle title={translate("title_loading")} />
					</div>
				)}
				{/* ошибка тестирования скрипта */}
				{(testingScript.status === RequestStatus.FAILED || testingScript.error === ResponseStatus.FAILED) && (
					<div className={styles.testScriptFailed}>
						{translate("title_error")}: {translate(testingScript.message || "title_loadFailed")}
					</div>
				)}
				{/* результат */}
				{testingScript.status === RequestStatus.IDLE && testingScript.data && (
					<ReactJson
						src={testingScript.data}
						name={false} // имя root объекта
						displayObjectSize={false} // кол-во элементов
						enableClipboard={false} // значок копирования
						displayDataTypes={false} // тип данных
					/>
				)}
			</DraggablePopoverComponent>
		</div>
	);
};

export default ScriptTest;
